import Papa from 'papaparse';

const DEFAULT_CSV_PATH = `${process.env.PUBLIC_URL || ''}/gigahorse-results.csv`;
const HEADER_PREFIX = 'GPU,Power (W),CPU,Difficulty,-r,k,C,OS,Giga Version,(Plot filter = 256),User,Information';

export const csvPath = process.env.REACT_APP_CSV_PATH || DEFAULT_CSV_PATH;

const trimValue = (value) => (typeof value === 'string' ? value.trim() : '');
const fallbackValue = (value) => trimValue(value) || 'N/A';

const parseNumericValue = (value) => {
  const normalized = trimValue(String(value)).replace(/,/g, '').replace(/[^0-9.]/g, '');
  const parsed = Number.parseFloat(normalized);

  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const normalizeCsvPayload = (csvText) => {
  const lines = csvText.split(/\r?\n/);
  const headerIndex = lines.findIndex((line) => line.trim().startsWith(HEADER_PREFIX));

  if (headerIndex === -1) {
    throw new Error('CSV header row not found.');
  }

  return lines.slice(headerIndex).join('\n');
};

const normalizeRow = (row) => {
  const gpuName = trimValue(row['GPU']);
  const kSize = trimValue(row['k']);
  const cLevel = trimValue(row['C']);
  const maxFarmSize = trimValue(row['(Plot filter = 256)']);

  if (!gpuName || !kSize || !cLevel || !maxFarmSize || gpuName === 'GPU') {
    return null;
  }

  const power = trimValue(row['Power (W)']);

  return {
    gpu_name: gpuName,
    power: power ? `${power}W` : 'N/A',
    cpu_used: fallbackValue(row['CPU']),
    difficulty: fallbackValue(row['Difficulty']),
    thread_count: fallbackValue(row['-r']),
    k_size: kSize,
    c_level: cLevel,
    operating_system: fallbackValue(row['OS']),
    giga_version: fallbackValue(row['Giga Version']),
    max_farm_size: maxFarmSize,
    user: fallbackValue(row['User']),
    information: fallbackValue(row['Information'])
  };
};

export const compareNumericStrings = (left, right) => parseNumericValue(left) - parseNumericValue(right);

export const compareGigaVersions = (left, right) => right.localeCompare(left, undefined, {
  numeric: true,
  sensitivity: 'base'
});

export const sortResults = (results) => [...results].sort((left, right) => (
  compareNumericStrings(left.c_level, right.c_level)
  || compareNumericStrings(left.max_farm_size, right.max_farm_size)
  || compareNumericStrings(left.power, right.power)
  || left.gpu_name.localeCompare(right.gpu_name, undefined, { numeric: true, sensitivity: 'base' })
));

export async function fetchGigahorseResults() {
  const response = await fetch(csvPath);

  if (!response.ok) {
    throw new Error(`CSV request failed with status ${response.status}`);
  }

  const csvText = await response.text();
  const parsed = Papa.parse(normalizeCsvPayload(csvText), {
    header: true,
    skipEmptyLines: true
  });

  return parsed.data.map(normalizeRow).filter(Boolean);
}
